import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import ValetApi from "../api/Api";
import "./ValidationPortal.css";

function formatCents(cents, currency = "usd") {
    const value = Number(cents || 0) / 100;
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: String(currency || "usd").toUpperCase(),
        }).format(value);
    } catch {
        return `$${value.toFixed(2)}`;
    }
}

function formatPercent(value) {
    const normalized = Number(value || 0);
    if (!Number.isFinite(normalized)) return "0.0%";
    return `${normalized.toFixed(1)}%`;
}

const TenantPortalHomePage = () => {
    const [profile, setProfile] = useState(null);
    const [usage, setUsage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        async function loadPortalData() {
            try {
                setIsLoading(true);
                const [profileRes, usageRes] = await Promise.all([ValetApi.getTenantValidationProfile(), ValetApi.getTenantValidationUsage({ range: "month" })]);

                if (!isMounted) return;
                setProfile(profileRes?.data?.tenant || null);
                setUsage(usageRes?.data?.usage || null);
            } catch (err) {
                if (!isMounted) return;
                setProfile(null);
                setUsage(null);
                toast.error(err?.[0] || err?.response?.data?.error?.message || "Unable to load tenant portal overview", { autoClose: 1700 });
            } finally {
                if (isMounted) setIsLoading(false);
            }
        }

        loadPortalData();
        return () => {
            isMounted = false;
        };
    }, []);

    const summary = usage?.summary || {};

    const printed = Number(summary.printedCount || 0);
    const redeemed = Number(summary.redeemedCount || 0);
    const redeemedRate = printed > 0 ? (redeemed / printed) * 100 : 0;

    const cards = useMemo(
        () => [
            {
                title: "Validate Ticket",
                body: "Look up by ticket or mobile and redeem one-time validation tokens.",
                to: "/validations/redeem",
                cta: "Open Validator",
            },
            {
                title: "Print QR Labels",
                body: "Generate a fresh batch and print Avery-ready labels for your desk team.",
                to: "/validations/tenants",
                cta: "Create Batch",
            },
            {
                title: "Usage and Invoice",
                body: "Review weekly or monthly usage and load invoice detail by month.",
                to: "/validations/reports",
                cta: "View Usage",
            },
        ],
        [],
    );

    return (
        <section className="page-scroll">
            <div className="page-scroll-inner validation-page-shell tenant-portal-shell">
                {isLoading ? (
                    <div className="tenant-portal-loading-wrap">
                        <Spinner color="primary" />
                    </div>
                ) : (
                    <>
                        <div className="tenant-portal-hero">
                            <h2>Tenant Validation Portal</h2>
                            <p>
                                {profile?.name || "Your team"} • {profile?.locationName || `Location ${profile?.locationId || "-"}`}
                            </p>
                        </div>

                        <div className="validation-kpi-grid tenant-portal-kpis">
                            <div className="validation-kpi-card">
                                <span className="validation-kpi-label">Printed This Month</span>
                                <strong>{printed}</strong>
                            </div>
                            <div className="validation-kpi-card">
                                <span className="validation-kpi-label">Redeemed This Month</span>
                                <strong>{redeemed}</strong>
                            </div>
                            <div className="validation-kpi-card">
                                <span className="validation-kpi-label">Redemption Rate</span>
                                <strong>{formatPercent(redeemedRate)}</strong>
                            </div>
                            <div className="validation-kpi-card">
                                <span className="validation-kpi-label">Redeemed Amount</span>
                                <strong>{formatCents(summary.redeemedAmountCents)}</strong>
                            </div>
                        </div>

                        <div className="tenant-portal-action-grid">
                            {cards.map((card) => (
                                <article
                                    key={card.title}
                                    className="tenant-portal-action-card">
                                    <h3>{card.title}</h3>
                                    <p>{card.body}</p>
                                    <Button
                                        tag={Link}
                                        to={card.to}
                                        color="primary">
                                        {card.cta}
                                    </Button>
                                </article>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default TenantPortalHomePage;
